import { useCallback, useState } from "react";

import { ObservabilityService, type ObservabilityWindow } from "@/api";
import { useApi } from "@/hooks/use-api";
import { useAuth } from "@/hooks/use-auth";
import { useTranslation } from "@/i18n";
import type { TranslationKey } from "@/i18n";
import { parseApiError } from "@/lib/api-error";
import { formatCompactNumberID } from "@/lib/format";
import { Alert } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Section } from "@/components/shared/section";
import { WindowSelector } from "./window-selector";

// Policy effects map to translation keys so the breakdown and
// summary tiles re-render on a locale switch. Unknown effects fall
// back to the raw key returned by the engine.
const EFFECT_KEY: Record<string, TranslationKey> = {
  allow: "observability.effects.allow",
  deny: "observability.effects.deny",
  require_hitl: "observability.effects.require_hitl",
  require_approval: "observability.effects.require_approval",
  redact: "observability.effects.redact",
  log_only: "observability.effects.log_only",
  escalate: "observability.effects.escalate",
};

export function PolicyTab() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [window, setWindow] = useState<ObservabilityWindow>("7d");

  const effectLabel = (effect: string): string => {
    const key = EFFECT_KEY[effect];
    return key ? t(key) : effect;
  };

  const fetcher = useCallback(
    () =>
      ObservabilityService.getPolicyEnforcement({
        window,
        workspace_id: user?.workspace_id ?? undefined,
      }),
    [window, user?.workspace_id]
  );

  const { data, error, isLoading } = useApi(fetcher, {
    deps: [window, user?.workspace_id],
  });

  const totalDecisions = data?.total_decisions ?? 0;
  const breakdown = data?.by_effect;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 tablet:flex-row tablet:items-center tablet:justify-between">
        <p className="text-sm text-neutral-600">
          {t("observability.policy.intro")}
        </p>
        <WindowSelector
          value={window}
          onChange={setWindow}
          windows={["24h", "7d", "30d"]}
        />
      </div>

      {error && <Alert variant="warning">{parseApiError(error)}</Alert>}

      <div className="grid grid-cols-2 gap-3 tablet:grid-cols-4">
        <div className="rounded-lg border border-ocean-100 bg-white p-4">
          <div className="label-eyebrow">
            {t("observability.policy.totalLabel")}
          </div>
          <div className="mt-1 text-2xl font-medium text-ocean-900">
            {isLoading && !data ? (
              <Skeleton className="h-7 w-20" />
            ) : (
              formatCompactNumberID(totalDecisions)
            )}
          </div>
        </div>
        {(["allow", "deny", "require_hitl"] as const).map((effect) => (
          <div
            key={effect}
            className="rounded-lg border border-ocean-100 bg-white p-4"
          >
            <div className="label-eyebrow">{effectLabel(effect)}</div>
            <div className="mt-1 text-2xl font-medium text-ocean-900">
              {isLoading && !data ? (
                <Skeleton className="h-7 w-20" />
              ) : (
                formatCompactNumberID(breakdown?.[effect] ?? 0)
              )}
            </div>
          </div>
        ))}
      </div>

      <Section
        title={t("observability.policy.breakdownTitle")}
        description={t("observability.policy.breakdownDescription")}
      >
        {isLoading && !data ? (
          <Skeleton className="h-32 w-full" />
        ) : breakdown ? (
          <div className="overflow-hidden rounded-lg border border-ocean-100 bg-white">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    {t("observability.policy.breakdownColEffect")}
                  </TableHead>
                  <TableHead>
                    {t("observability.policy.breakdownColCount")}
                  </TableHead>
                  <TableHead>
                    {t("observability.policy.breakdownColPercentage")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Object.entries(breakdown).map(([effect, count]) => {
                  const numericCount = Number(count);
                  const percent =
                    totalDecisions > 0
                      ? ((numericCount / totalDecisions) * 100).toFixed(1)
                      : "0.0";
                  return (
                    <TableRow key={effect}>
                      <TableCell className="font-medium text-ocean-900">
                        {effectLabel(effect)}
                      </TableCell>
                      <TableCell>
                        {formatCompactNumberID(numericCount)}
                      </TableCell>
                      <TableCell className="text-neutral-600">
                        {percent}%
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        ) : (
          <Alert variant="info">
            {t("observability.policy.breakdownEmpty")}
          </Alert>
        )}
      </Section>

      <Section
        title={t("observability.policy.topTitle")}
        description={t("observability.policy.topDescription")}
      >
        {isLoading && !data ? (
          <Skeleton className="h-32 w-full" />
        ) : (
          <div className="overflow-hidden rounded-lg border border-ocean-100 bg-white">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    {t("observability.policy.topColName")}
                  </TableHead>
                  <TableHead>
                    {t("observability.policy.topColHits")}
                  </TableHead>
                  <TableHead>
                    {t("observability.policy.topColEffect")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!data || data.top_triggered_policies.length === 0 ? (
                  <TableEmpty
                    colSpan={3}
                    message={t("observability.policy.topEmpty")}
                  />
                ) : (
                  data.top_triggered_policies.map((policy) => (
                    <TableRow key={policy.policy_name}>
                      <TableCell className="font-medium text-ocean-900">
                        {policy.policy_name}
                      </TableCell>
                      <TableCell>
                        {formatCompactNumberID(policy.hit_count)}
                      </TableCell>
                      <TableCell className="text-neutral-600">
                        {effectLabel(policy.effect)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </Section>
    </div>
  );
}
